import {
  ThemeProvider,
  Button,
  IconButton,
  ToggleButtonGroup,
  Tooltip,
  ToggleButton,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Typography,
  Box,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Vocabulary } from "../Utils/Vocabulary";
import genericStyles from "../Styles/genericStyles.module.css";
import { Edit, Delete, ViewColumn, Search, Print, Download, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import GenericModal from "./GenericModal";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import * as XLSX from "xlsx";
import { deleteData } from "../Services/deleteData";
import { getData } from "../Services/getData";
import { useNavigate } from "react-router-dom";
import { ETableEditAction } from "../Utils/Enums";
import { MUIDataTableCustomStyleProps } from "../Utils/Types";
import StandardActionButtons from "./StandarsActionButtons";
import { isMobile, formatDate } from "../Utils/Utils";
import useQueryStringParser from "../Hooks/useQueryStringParser";
import Config from "../Utils/Config";
import useCustomSearchParam from "../Hooks/useCustomSearchParam";
import { tableTheme } from "../Utils/Constants";
import { WebSocketContext } from "../Context/WebSocketContext";


export default function MUIDataTableCustomStyle(
  props: MUIDataTableCustomStyleProps
) {
  const theme = useTheme();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [tableData, setTableData]: any = useState([]);
  const tableDataRef = useRef<any[]>([]);
  const [expandedSubRows, setExpandedSubRows] = useState<Set<string>>(new Set());
  const [count, setCount] = useState(0);
  const [footerFields, setFooterFields] = useState([]);
  const [perPage, setPerPage] = useState(Config.rowsPerPage);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const navigate = useNavigate();
  const customSearchParams = useCustomSearchParam();
  const [searchText, setSearchText] = useState("");
  const websocketContext = useContext(WebSocketContext);
  const [queryFilters, setQueryFilters] = useState({});
  const [isNavigating, setIsNavigating] = useState(false);
  const [isMobileView, setIsMobileView] = useState(isMobile());
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState<null | HTMLElement>(null);
  const [rowsSelected, setRowsSelected] = useState<number[]>([]);

  /**
   * Check if the current user is an admin (cached)
   */
  const isAdmin = useMemo(() => {
    const userRolesJson = localStorage.getItem("userRoles");
    if (!userRolesJson) return false;
    const userRoles = JSON.parse(userRolesJson);
    return userRoles.some((role: { name: string }) => role.name === "Admin");
  }, []);

  /**
   * Handle window resize to update mobile view
   */
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useQueryStringParser((data: any) => {
    if (data) {
      setQueryFilters(data);
      setRowsSelected([]);
      getDataTable(data);
    }
  });

  /**
   * Adds a URL parameter to the provided Map if it doesn't already exist.
   * @param key - The key for the URL parameter.
   * @param value - The value of the URL parameter.
   * @param addedParams - The Map to store the parameters.
   */
  const addUrlParam = (
    key: string,
    value: string | number | boolean | null | undefined,
    addedParams: Map<string, string>
  ) => {
    if (value !== undefined && value !== null && !addedParams.has(key)) {
      addedParams.set(key, encodeURIComponent(value.toString())); // Add new parameter to the Map if it doesn't exist
    }
  };

  /**
   *
   * @param options
   * @returns
   */
  const generateURL = (options?: Record<string, any>) => {
    // Parse the query parameters from window.location.search
    const urlParams = new URLSearchParams(window.location.search);
    const addedParams = new Map<string, string>(); // Tracks added parameters
    // Determine the page and perPage values
    const page = options?.page
      ? parseInt(options.page)
      : parseInt(urlParams.get("page") || Config.standardPage);

    const perPage = options?.perPage
      ? parseInt(options.perPage)
      : parseInt(urlParams.get("perPage") || Config.rowsPerPage);

    // Use the Map to track added params and avoid duplicates
    addUrlParam("page", page, addedParams);
    addUrlParam("perPage", perPage, addedParams);

    if (options && Object.keys(options).length > 0) {
      // Add the query parameters from options to the URL (these will override any parameters from urlParams)
      for (const key in options) {
        if (Object.prototype.hasOwnProperty.call(options, key)) {
          const value = options[key];
          addUrlParam(key, value, addedParams);
        }
      }
    } else {
      if (Object.keys(urlParams).length > 0)
        // Add the query parameters from urlParams to the URL (this includes all existing query params)
        urlParams.forEach((value, key) => {
          addUrlParam(key, value, addedParams);
        });
    }

    // Add the filters from props.filters, ensuring no duplicates from options or urlParams
    if (props.filters) {
      for (const key in props.filters) {
        const value = props.filters[key];
        addUrlParam(key, value, addedParams);
      }
    }

    if (props.CRUDEndPoint) {
      const baseUrl = props.CRUDEndPoint;
      const hasQuery = baseUrl.includes("?");
      const hasPaginated = baseUrl.includes("/paginated");

      const urlQuery = Array.from(addedParams.entries())
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      let url: string;

      if (hasPaginated) {
        // Already contains /paginated
        url = hasQuery ? `${baseUrl}&${urlQuery}` : `${baseUrl}?${urlQuery}`;
      } else {
        // Doesn't contain /paginated yet
        const endpoint = baseUrl.endsWith("/")
          ? `${baseUrl}paginated`
          : `${baseUrl}/paginated`;
        url = `${endpoint}?${urlQuery}`;
      }

      return url;
    }
  };

  /**
   *
   * @param options
   * @returns
   */
  const getDataTable = async (options?: Record<string, any>) => {
    const url = generateURL(options);
    if (url) {
      const res = (await getData(url)) as any;
      if (!res || res?.error) {
        return;
      }
      setTableData(res.data.results);
      tableDataRef.current = res.data.results;
      setCount(res.data.numberOfResults);
      setFooterFields(res.data.footerFields);
    }
  };

  /**
   *
   * @param data
   * @returns
   */
  const customGetDataTable = async (data: any) => {
    if (data) {
      const url = data;
      const res = (await getData(url)) as any;
      if (!res || res?.error) {
        return;
      }
      setTableData(res.data.results);
      tableDataRef.current = res.data.results;
      setCount(res.data.numberOfResults);
    } else {
      // getDataTable();
    }
  };

  /**
   *
   */
  useEffect(() => {
    if (props.customTriggerReload !== undefined) {
      //TO DO: should consider existing filters or delete them from url
      setRowsSelected([]);
      customGetDataTable(props.reloadURL);
    }
  }, [props.customTriggerReload]);

  /**
   *
   */
  useEffect(() => {
    if (props.triggerReload !== undefined) {
      //if query contains params remove them, else get data
      setRowsSelected([]);
      getDataTable(queryFilters);
    }
  }, [props.triggerReload]);

  /**
   *
   * @param id
   */
  const deleteElement = async () => {
    const url = `${props.CRUDEndPoint}/${selectedId}`;
    const res = (await deleteData(url)) as any;
    if (!res || res?.error) {
      return;
    }
    if (props.shouldTriggerStorageUpdate) {
      websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
    }
    setSelectedId(null);
    setOpenDeleteModal(false);
    if (props.setContextValue) props.setContextValue([]);
    if (props?.handleInlineForm) props?.handleInlineForm();
    if (props?.setTriggerReload) props?.setTriggerReload();
    else getDataTable(queryFilters);
  };

  /**
   * Actions columns constant
   */
  const actionsColumns = [
    ...props.columns,
    {
      label: "",
      name: "",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        filter: false,
        sort: false,
        display: props.hideOptions ? false : true,
        empty: true,
        customBodyRenderLite: (rowIndex: number, value: any) => {
          return (
            <>
              <div style={{ float: "right" }}>
                <ToggleButtonGroup exclusive aria-label="text alignment" style={{ flexWrap: "wrap" }}>
                {props.renderCustomColumns
                    ? props.renderCustomColumns(rowIndex, tableData[rowIndex])
                    : null}
                  {props?.hideEditButton ? null : (
                    <Tooltip title={Vocabulary.edit}>
                      <ToggleButton
                        onClick={(e: any) => {
                          handleEditEntity(rowIndex, e);
                        }}
                        value="center"
                        aria-label="centered"
                      >
                        <Edit />
                      </ToggleButton>
                    </Tooltip>
                  )}
                  {props.hideDeleteButton ? null : (
                    <Tooltip title={Vocabulary.delete}>
                      <ToggleButton
                        disabled={
                          tableData[rowIndex]?.shouldDelete === false ||
                          (props.rowDeleteValidation && !props.rowDeleteValidation(tableData[rowIndex]))
                        }
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setOpenDeleteModal(true);
                          setSelectedId(tableData[rowIndex]._id);
                        }}
                        value="center"
                        aria-label="centered"
                      >
                        <Delete />
                      </ToggleButton>
                    </Tooltip>
                  )}
                </ToggleButtonGroup>
              </div>
            </>
          );
        },
      },
    },
  ];

  /**
   *
   */
  const handleAddEntity = () => {
    const type = props.onAddAction ?? props.onEditAction;
    switch (type) {
      case ETableEditAction.MODAL:
        if (props?.handleModalForm) props?.handleModalForm(true);
        break;
      case ETableEditAction.REDIRECT: {
        const addUrl = props.addURL || props.rowClickURL || "";
        navigate(addUrl);
        break;
      }
      case ETableEditAction.INLINE:
        return props?.handleInlineForm ? props?.handleInlineForm() : null;
      default:
        break;
    }
  };

  /**
   *
   * @param rowIndex
   * @param e
   */
  const handleEditEntity = (rowIndex: number, e: any) => {
    const type = props.onEditAction;
    switch (type) {
      case ETableEditAction.MODAL:
        e.stopPropagation();
        if (props.setContextValue) props.setContextValue(tableData[rowIndex]);
        if (props?.handleModalForm) props?.handleModalForm(true);
        if (props.addEntityIdToURL) {
          customSearchParams.searchParams.set("id", tableData[rowIndex]._id);
          customSearchParams.setSearchParams(customSearchParams.searchParams);
        }
        break;
      case ETableEditAction.REDIRECT: {
        e.stopPropagation();
        const editUrl = props.editURL || props.rowClickURL || "";
        const link = document.createElement("a");
        link.href = `/${editUrl.replace(/^\/|\/$/g, "")}/${tableData[rowIndex]._id}`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
      }
      case ETableEditAction.INLINE:
        return props?.handleInlineForm
          ? props?.handleInlineForm(tableData[rowIndex])
          : null;
      default:
        break;
    }
  };



  /**
   *
   * @returns
   */
  const getTableOptions = () => {
    const responsive: "standard" | "vertical" | "simple" | undefined =
      isMobileView && !props.disableVerticalMobile ? "vertical" : "standard";
    return {
      customToolbarSelect: props.customToolbarSelect,
      onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any) => {
        setRowsSelected(allRowsSelected.map((row: any) => row.dataIndex));

        if (!props.onRowSelectionChange) {
          return;
        }

        const selectedRowData = (allRowsSelected || []).map((row: any) =>
          tableData[row.dataIndex]
        );
        props.onRowSelectionChange(allRowsSelected, selectedRowData);
      },
      selectableRows: props.selectableRows ? props.selectableRows : "none",
      rowsSelected: rowsSelected,
      responsive: responsive,
      viewColumns: props.viewColumns || false,
      rowsPerPage: perPage,
      page: parseInt(new URLSearchParams(window.location.search).get("page") || "0"),
      rowsPerPageOptions: Config.rowsPerPageOptions,
      confirmFilters: true,
      filter: false,
      print: false,
      download: false,
      sort: true,
      fixedHeader: true,
      serverSide: true,
      searchOpen: false,
      search: false,
      searchText: searchText,
      jumpToPage: true,
      textLabels: {
        body: {
          noMatch: Vocabulary.noResultsFound,
        },
        pagination: {
          next: Vocabulary.nextPage,
          previous: Vocabulary.previousPage,
          rowsPerPage: Vocabulary.rowsPerPage,
          displayRows: Vocabulary.of,
          jumpToPage: Vocabulary.goToPage,
        },
        toolbar: {
          search: Vocabulary.search,
          downloadCsv: Vocabulary.downloadCSV,
          print: Vocabulary.print,
        },
        selectedRows: {
          text: Vocabulary.rowsSelected,
          delete: Vocabulary.delete,
        },
      },
      searchProps: {
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            const currentSearchValue = e.target.value;
            if (currentSearchValue && currentSearchValue.trim() !== "") {
              customSearchParams.addParamToQueryString(
                "search",
                currentSearchValue.trim()
              );
              const updatedFilters = {
                ...queryFilters,
                search: currentSearchValue.trim(),
                page: 0,
              };
              getDataTable(updatedFilters);
            } else {
              const updatedFilters = {
                ...queryFilters,
                search: "",
                page: 0,
              } as { [key: string]: any };
              delete updatedFilters.search;
              getDataTable(updatedFilters);
            }
            window.scrollTo(0, 0);
          }
        },
      },
      onChangePage: (pageNumber: number) => {
        customSearchParams.addParamToQueryString("page", pageNumber);
        setRowsSelected([]);
        if (window.scrollTo) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      },
      onChangeRowsPerPage: (numberOfRows: number) => {
        setPerPage(numberOfRows);
        setRowsSelected([]);
        const newParams = new URLSearchParams(customSearchParams.searchParams.toString());
        newParams.set("page", "0");
        newParams.set("perPage", numberOfRows.toString());
        customSearchParams.setSearchParams(newParams);
        if (window.scrollTo) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      },
      setRowProps: props.options && props.options.setRowProps
        ? props.options.setRowProps
        : (row: any, _: any, rowIndex: any) => {
            return {
              style: {
                cursor: props.onRowClick ? "pointer" : "auto",
                backgroundColor: rowIndex % 2 ? "#fff" : "#ebeded",
              },
              className: props.rowProps ? props.rowProps(row) : null,
            };
          },
      onSearchChange: async (search: any) => {
        setSearchText(search);
        setRowsSelected([]);
        if (!search || search?.length === 0) {
          customSearchParams.removeParamFromQueryString(["search"]);
        } else {
          customSearchParams.addParamToQueryString("search", search);
          customSearchParams.addParamToQueryString("page", "0");
        }
      },
      count: count,
      onRowsDelete: (rowData: any) => {
        //ask for confirmation
        const data = rowData?.data?.map(
          (row: any) => tableData[row.dataIndex]._id
        );
        setSelectedId(data.join(","));
        setOpenDeleteModal(true);
      },
      onRowClick: props.onRowClick
        ? (_: any, rowState: any) => {
            if (props.onRowClick && !isNavigating) {
              setIsNavigating(true);
              props?.onRowClick(tableData[rowState.rowIndex]);
              setTimeout(() => setIsNavigating(false), 1000);
            }
          }
        : () => {
            return;
          },
      expandableRows: props.expandableRows,
      expandableRowsHeader: props.expandableRowsHeader,
      isRowExpandable: (dataIndex: number) => {
        if (!props.expandableRowObjectLocationName) return false;
        const parts = props.expandableRowObjectLocationName.split(".");
        const data = parts.reduce(
          (acc: any, key: string) =>
            acc && acc[key] !== undefined ? acc[key] : undefined,
          tableData[dataIndex],
        );
        return Array.isArray(data) && data.length > 0;
      },
      renderExpandableRow: (_: any, rowMeta: any) => {
        if (props.expandableRowObjectLocationName) {
          const expandableRowObjectLocationNameParts =
            props.expandableRowObjectLocationName.split(".");
          const dataDetails = expandableRowObjectLocationNameParts.reduce(
            (acc, key) =>
              acc && acc[key] !== undefined ? acc[key] : undefined,
            tableData[rowMeta.dataIndex] // Start from the root object
          );

          if (!dataDetails || !Array.isArray(dataDetails) || dataDetails.length == 0) return null;
          if (!props.expandableColumns || props.expandableColumns.length === 0) return null;

          return (
            <>
              {/* Header Row for Expandable Section */}
              <TableRow>
                {/* Empty cell at the beginning */}
                <TableCell style={{ backgroundColor: "#c5c8d2" }} />
                {props.expandableColumns.map((col: any) => (
                  <TableCell
                    key={col.name}
                    style={{ backgroundColor: "#c5c8d2" }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>

              {/* Data Rows + optional collapsible sub-rows */}
              {dataDetails.map((det: any, index: number) => {
                const subRows: any[] =
                  props.expandableSubRowsKey && Array.isArray(det[props.expandableSubRowsKey])
                    ? det[props.expandableSubRowsKey]
                    : [];
                const subKey = `${rowMeta.dataIndex}-${index}`;
                const isSubExpanded = expandedSubRows.has(subKey);
                const toggleSub = () => {
                  setExpandedSubRows((prev) => {
                    const next = new Set(prev);
                    next.has(subKey) ? next.delete(subKey) : next.add(subKey);
                    return next;
                  });
                };
                return (
                  <React.Fragment key={index}>
                    {/* Product row */}
                    <TableRow
                      onClick={() => props.onExpandableRowClick?.(det)}
                      style={{ cursor: props.onExpandableRowClick ? "pointer" : "default" }}
                    >
                      <TableCell style={{ backgroundColor: "#e5e7ef" }}>
                        {subRows.length > 0 && props.expandableSubColumns && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleSub(); }}>
                            <ChevronRightIcon
                              fontSize="small"
                              style={{ transition: "transform 0.2s", transform: isSubExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                            />
                          </IconButton>
                        )}
                      </TableCell>
                      {props.expandableColumns.map((col: any) => (
                        <TableCell key={col.name} style={{ backgroundColor: "#e5e7ef" }}>
                          {col.options?.customBodyRender
                            ? col.options.customBodyRender(det[col.name])
                            : det[col.name]}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Sub-rows (students) — shown only when expanded */}
                    {isSubExpanded && subRows.map((sub: any, subIndex: number) => (
                      <TableRow
                        key={`sub-${subIndex}`}
                        onClick={() => props.onExpandableSubRowClick?.(sub)}
                        style={{ cursor: props.onExpandableSubRowClick ? "pointer" : "default" }}
                      >
                        <TableCell style={{ backgroundColor: "#f3f4f8", paddingLeft: 64 }} />
                        {props.expandableSubColumns!.map((col: any) => (
                          <TableCell key={col.name} style={{ backgroundColor: "#f3f4f8" }}>
                            {col.options?.customBodyRender
                              ? col.options.customBodyRender(sub[col.name])
                              : sub[col.name]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </>
          );
        }
      },

      customToolbar: () => {
        if (props.hideToolbar) return null;

        // On mobile, render compact button in actions section
        if (isMobileView) {
          return (
            <div style={{ display: "flex", gap: 4 }}>
              {props?.hideAddButton ? null : (
                <Button
                  type="button"
                  variant="contained"
                  className={genericStyles.addButton}
                  onClick={handleAddEntity}
                  sx={{
                    minWidth: "auto",
                    padding: "8px",
                  }}
                >
                  <AddCircleIcon />
                </Button>
              )}
              {props.showPrintButton && isAdmin && (
                <Tooltip title={Vocabulary.print}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                    sx={{ minWidth: "auto", padding: "8px" }}
                  >
                    <Print />
                  </Button>
                </Tooltip>
              )}
              {props.showDownloadButton && isAdmin && (
                <Tooltip title={Vocabulary.download}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                    sx={{ minWidth: "auto", padding: "8px" }}
                  >
                    <Download />
                  </Button>
                </Tooltip>
              )}
            </div>
          );
        }

        return (
          <div style={{ display: "flex", gap: 8 }}>
            {props?.hideAddButton ? null : (
              <Button
                type="button"
                variant="contained"
                className={genericStyles.addButton}
                onClick={handleAddEntity}
              >
                <AddCircleIcon />
                <div style={{ marginLeft: 5 }}>{Vocabulary.add}</div>
              </Button>
            )}
            {props.showPrintButton && isAdmin && (
              <Button
                variant="contained"
                color="secondary"
                onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                startIcon={<Print />}
              >
                {Vocabulary.print}
              </Button>
            )}
            {props.showDownloadButton && isAdmin && (
              <Button
                variant="contained"
                color="success"
                onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                startIcon={<Download />}
              >
                {Vocabulary.download}
              </Button>
            )}
            {React.isValidElement(props.buttons)
              ? props.buttons
              : props.buttons &&
                props.buttons?.map(
                  (button: {
                    label: string;
                    handleClick: void;
                    icon: JSX.Element;
                  }, index: number) => (
                    <Button
                      key={index}
                      variant="contained"
                      className={genericStyles.addButton}
                      onClick={() => button.handleClick}
                    >
                      {button.icon && button.icon}
                      <div style={{ marginLeft: 5 }}>
                        {button.label && button.label}
                      </div>
                    </Button>
                  )
                )}
          </div>
        );
      },
      ...(props.customRowRender && {
        customRowRender: (rowData: any, dataIndex: any, rowIndex: any) => {
          return props.customRowRender
            ? props.customRowRender(rowData, dataIndex, rowIndex, tableData)
            : null;
        },
      }),
      ...(props.customFooter && {
        customTableBodyFooterRender: () => props.customFooter?.(footerFields),
      }),
      onColumnSortChange: (columnName: string, direction: "asc" | "desc") => {
        // If the column has a custom sort field defined in options, use it.
        // Otherwise, fall back to the column name itself.
        const column =
          props.columns.find(
            (col: any) => col.name === columnName && col.options?.sortField
          )?.options?.sortField || columnName;

        // Create a new filters object, updating the sort field and direction.
        // Also reset the page to 0 whenever sorting changes.
        const updatedFilters = {
          ...queryFilters,
          sortBy: `${column},${direction}`,
          page: 0,
        };
        setRowsSelected([]);
        getDataTable(updatedFilters);
      },
      // Merge any custom options passed from props
      ...props.options,
    };
  };

  /**
   * Gets export params including date range from URL
   */
  const getExportParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    let startDate = "";
    let endDate = "";

    urlParams.forEach((value, key) => {
      if (key === "start") startDate = value;
      if (key === "end") endDate = value;
    });

    const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : "";
    const exportTitle = props.exportTitle
      ? `${props.exportTitle}${dateRange ? ` ${dateRange}` : ""}`
      : `${props.title}${dateRange ? ` ${dateRange}` : ""}`;

    return { startDate, endDate, exportTitle };
  };

  /**
   * Fetches all data for export (ignoring pagination)
   */
  const fetchAllDataForExport = async (): Promise<any[]> => {
    const url = generateURL({ ...queryFilters, page: 0, perPage: 100000 });
    if (url) {
      const res = (await getData(url)) as any;
      if (!res || res?.error) {
        return tableData; // Fallback to current page data
      }
      return res.data.results || [];
    }
    return tableData;
  };

  /**
   * Handles print functionality for the table
   * @param currentPageOnly - If true, prints only the current page data
   */
  const handlePrint = async (currentPageOnly = false) => {
    setPrintMenuAnchor(null);
    const { exportTitle } = getExportParams();

    // Fetch data based on the selected option
    const dataToPrint = currentPageOnly ? tableData : await fetchAllDataForExport();

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const visibleColumns = props.columns.filter(
      (col: any) => col.options?.display !== false && col.options?.display !== "false"
    );

    const tableHeaders = visibleColumns.map((col: any) => col.label || col.name);

    const tableRows = dataToPrint
      .map((row: any) => {
        const cells = visibleColumns
          .map((col: any) => {
            // Use exportValue function if provided, otherwise fall back to raw value
            const value = col.options?.exportValue
              ? col.options.exportValue(row[col.name], row)
              : row[col.name];
            if (value instanceof Date || (typeof value === "string" && !isNaN(Date.parse(value)) && col.name.toLowerCase().includes("date"))) {
              return formatDate(value);
            }
            return value ?? "";
          })
          .map((val: any) => `<td>${val}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    // Generate footer HTML if exportFooterFields is provided
    let footerHtml = "";
    if (props.exportFooterFields && footerFields) {
      const exportedFooter = props.exportFooterFields(footerFields);
      if (exportedFooter && exportedFooter.length > 0) {
        footerHtml = `
          <table class="footer-table">
            ${exportedFooter.map((field) => `
              <tr>
                <td><strong style="color: ${field.color || 'black'}">${field.label}:</strong></td>
                <td><strong>${field.value}</strong></td>
              </tr>
            `).join("")}
          </table>
        `;
      }
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${exportTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
            h1 { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #000; padding: 6px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .footer-table { width: auto; margin-top: 20px; }
            .footer-table td { padding: 8px 15px; border: none; }
            @media print { body { margin: 10px; } h1 { font-size: 16px; } table { font-size: 10px; } }
          </style>
        </head>
        <body>
          <h1>${exportTitle}</h1>
          <table>
            <thead><tr>${tableHeaders.map((h: string) => `<th>${h}</th>`).join("")}</tr></thead>
            <tbody>${tableRows}</tbody>
          </table>
          ${footerHtml}
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  /**
   * Handles download to Excel functionality
   * @param currentPageOnly - If true, downloads only the current page data
   */
  const handleDownload = async (currentPageOnly = false) => {
    setDownloadMenuAnchor(null);
    const { startDate, endDate } = getExportParams();

    // Fetch data based on the selected option
    const dataToExport = currentPageOnly ? tableData : await fetchAllDataForExport();

    const visibleColumns = props.columns.filter(
      (col: any) => col.options?.display !== false && col.options?.display !== "false"
    );

    const excelData = dataToExport.map((row: any) => {
      const rowData: Record<string, any> = {};
      visibleColumns.forEach((col: any) => {
        const label = col.label || col.name;
        // Use exportValue function if provided, otherwise fall back to raw value
        let value = col.options?.exportValue
          ? col.options.exportValue(row[col.name], row)
          : row[col.name];
        if (value instanceof Date || (typeof value === "string" && !isNaN(Date.parse(value)) && col.name.toLowerCase().includes("date"))) {
          value = formatDate(value);
        }
        rowData[label] = value ?? "";
      });
      return rowData;
    });

    // Add footer fields if exportFooterFields is provided
    if (props.exportFooterFields && footerFields) {
      const exportedFooter = props.exportFooterFields(footerFields);
      if (exportedFooter && exportedFooter.length > 0) {
        const firstColumnLabel = visibleColumns[0]?.label || visibleColumns[0]?.name || "";
        const secondColumnLabel = visibleColumns[1]?.label || visibleColumns[1]?.name || "";

        // Add empty row separator
        excelData.push({});

        // Add footer rows
        exportedFooter.forEach((field) => {
          excelData.push({
            [firstColumnLabel]: field.label,
            [secondColumnLabel]: field.value,
          });
        });
      }
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, props.title?.substring(0, 31) || "Data");

    // Generate timestamp if exportWithTimestamp is enabled (using local time)
    const timestamp = props.exportWithTimestamp
      ? `_${new Date().toLocaleString("sv-SE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit"
        }).replace(/[: ]/g, "-")}`
      : "";

    const fileName = startDate && endDate
      ? `${props.title || "export"}_${startDate}_${endDate}${timestamp}.xlsx`
      : `${props.title || "export"}${timestamp}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const CustomViewColumnIcon = () => {
    return (
      <>
        <ViewColumn />
        <span style={{ marginLeft: 5, fontSize: 10 }}>
          {Vocabulary.view}
          <br />
          {Vocabulary.columns}
        </span>
      </>
    );
  };

  const components: any = {
    icons: {
      ViewColumnIcon: CustomViewColumnIcon,
    },
    ...(props.expandableRowObjectLocationName && {
      ExpandButton: ({ dataIndex, onExpand, ...rest }: any) => {
        const parts = props.expandableRowObjectLocationName!.split(".");
        const row = tableDataRef.current[dataIndex];
        const data = parts.reduce(
          (acc: any, key: string) =>
            acc && acc[key] !== undefined ? acc[key] : undefined,
          row,
        );
        const isExpandable = Array.isArray(data) && data.length > 0;
        if (!isExpandable) return <span style={{ display: "inline-flex", width: 32 }} />;
        return (
          <IconButton size="small" onClick={onExpand} {...rest}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        );
      },
    }),
  };

  const customTitle = () => {
    if (!props.title && props.hideSearch && props.hideAddButton) return null;
    
    const mobile = isMobileView;
    
    // Mobile layout: Two rows
    if (mobile) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            margin: 0,
            padding: 0,
          }}
        >
          {/* Title */}
          {props.title && (
            <Typography 
              variant="h6" 
              component="div"
              sx={{
                margin: 0,
                padding: 0,
                textAlign: "left",
              }}
            >
              {props.title}
            </Typography>
          )}
          
          {/* Search Bar */}
          {!props.hideSearch && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                margin: 0,
                padding: 0,
              }}
            >
              <TextField
                size="small"
                placeholder={Vocabulary.search}
                value={searchText}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchText(value);
                  if (!value || value.length === 0) {
                    customSearchParams.removeParamFromQueryString(["search"]);
                  } else {
                    customSearchParams.addParamToQueryString("search", value);
                    customSearchParams.addParamToQueryString("page", "0");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const currentSearchValue = (e.target as HTMLInputElement).value;
                    if (currentSearchValue && currentSearchValue.trim() !== "") {
                      customSearchParams.addParamToQueryString(
                        "search",
                        currentSearchValue.trim()
                      );
                      const updatedFilters = {
                        ...queryFilters,
                        search: currentSearchValue.trim(),
                        page: 0,
                      };
                      getDataTable(updatedFilters);
                    } else {
                      const updatedFilters = {
                        ...queryFilters,
                        search: "",
                        page: 0,
                      } as { [key: string]: any };
                      delete updatedFilters.search;
                      getDataTable(updatedFilters);
                    }
                    window.scrollTo(0, 0);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flexGrow: 1,
                  width: "100%",
                  margin: 0,
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: `${theme.palette.primary.main} !important`,
                    },
                  },
                }}
              />
              {/* Additional custom buttons from props */}
              {React.isValidElement(props.buttons)
                ? props.buttons
                : props.buttons &&
                  props.buttons?.map(
                    (button: {
                      label: string;
                      handleClick: void;
                      icon: JSX.Element;
                    }, index: number) => (
                      <Button
                        key={index}
                        variant="contained"
                        className={genericStyles.addButton}
                        onClick={() => button.handleClick}
                        sx={{
                          minWidth: "auto",
                          padding: "8px",
                        }}
                      >
                        {button.icon && button.icon}
                      </Button>
                    )
                  )}
            </Box>
          )}
        </Box>
      );
    }
    
    // Desktop layout: Single row with title on left, search on right
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
        }}
      >
        {props.title && (
          <Typography 
            variant="h6" 
            component="div"
            sx={{
              flexShrink: 0,
            }}
          >
            {props.title}
          </Typography>
        )}
        {!props.hideSearch && (
          <TextField
            size="small"
            placeholder={Vocabulary.search}
            value={searchText}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);
              if (!value || value.length === 0) {
                customSearchParams.removeParamFromQueryString(["search"]);
              } else {
                customSearchParams.addParamToQueryString("search", value);
                customSearchParams.addParamToQueryString("page", "0");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const currentSearchValue = (e.target as HTMLInputElement).value;
                if (currentSearchValue && currentSearchValue.trim() !== "") {
                  customSearchParams.addParamToQueryString(
                    "search",
                    currentSearchValue.trim()
                  );
                  const updatedFilters = {
                    ...queryFilters,
                    search: currentSearchValue.trim(),
                    page: 0,
                  };
                  getDataTable(updatedFilters);
                } else {
                  const updatedFilters = {
                    ...queryFilters,
                    search: "",
                    page: 0,
                  } as { [key: string]: any };
                  delete updatedFilters.search;
                  getDataTable(updatedFilters);
                }
                window.scrollTo(0, 0);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 450,
              maxWidth: 600,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: `${theme.palette.primary.main} !important`,
                },
              },
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <div>
      <ThemeProvider theme={tableTheme}>
        <MUIDataTable
          title={customTitle()}
          data={tableData}
          columns={actionsColumns}
          options={getTableOptions()}
          components={components}
        />
      </ThemeProvider>
      <GenericModal
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        style={{ zIndex: 9999 }}
        title={Vocabulary.confirmDeleteTitle}
        actions={
          <StandardActionButtons
            okAction={() => deleteElement()}
            cancelAction={() => setOpenDeleteModal(false)}
            okText={Vocabulary.yes}
            cancelText={Vocabulary.no}
          />
        }
      >
        <div>{Vocabulary.confirmDeleteMessage}</div>
      </GenericModal>
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleDownload(true)}>
          {Vocabulary.downloadCurrentPage}
        </MenuItem>
        <MenuItem onClick={() => handleDownload(false)}>
          {Vocabulary.downloadAllData}
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={() => setPrintMenuAnchor(null)}
      >
        <MenuItem onClick={() => handlePrint(true)}>
          {Vocabulary.printCurrentPage}
        </MenuItem>
        <MenuItem onClick={() => handlePrint(false)}>
          {Vocabulary.printAllData}
        </MenuItem>
      </Menu>
      {props.addOrEditForm}
    </div>
  );
}
